/**
 * @module components/Controls/BulkTaskUploadModal
 * @description Modal de carga masiva de tareas desde archivo CSV para un control ISO.
 *
 * Permite importar múltiples tareas de una vez mediante un CSV con columnas:
 * título, descripción, email_responsable, fecha_vencimiento, prioridad.
 *
 * ## Flujo de importación
 * 1. Usuario descarga la plantilla CSV de ejemplo
 * 2. Completa los datos y sube el archivo
 * 3. El parser valida cada fila y resuelve el email a un `TechnicalReferent`
 * 4. Se muestran las filas válidas e inválidas antes de confirmar
 * 5. Al confirmar, se llaman `addTaskToControl()` por cada fila válida
 *
 * Las filas con errores (email no encontrado, fecha inválida, prioridad inválida)
 * se muestran en rojo con el mensaje de error específico y no se importan.
 *
 * @see DataContext.addTaskToControl para la creación individual de tareas
 * @see DetailPanel que abre este modal desde la pestaña de tareas
 */
import React, { useState, useRef } from 'react';
import { X, Download, Upload, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { SelectedControl, Task, TechnicalReferent } from '../../types/iso';

// =====================================================
// TIPOS INTERNOS
// =====================================================

interface ParsedRow {
  rowNum: number;
  titulo: string;
  descripcion: string;
  email_responsable: string;
  fecha_vencimiento: string;
  prioridad: 'low' | 'medium' | 'high' | 'critical';
  errors: string[];
  referent?: TechnicalReferent;
}

interface ImportResult {
  rowNum: number;
  titulo: string;
  success: boolean;
  error?: string;
}

type Step = 'upload' | 'preview' | 'importing' | 'done';

// =====================================================
// PROPS
// =====================================================

interface BulkTaskUploadModalProps {
  control: SelectedControl;
  onClose: () => void;
  technicalReferents: TechnicalReferent[];
  addTaskToControl: (
    controlId: string,
    task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>
  ) => Promise<void>;
  toast?: {
    success: (message: string, description?: string) => void;
    error: (message: string, description?: string) => void;
    warning: (message: string, description?: string) => void;
    info: (message: string, description?: string) => void;
  };
}

// =====================================================
// HELPERS
// =====================================================

const VALID_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;

/** Parsea una línea CSV respetando comillas dobles */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

/** Normaliza prioridad: acepta español e inglés */
function normalizePriority(raw: string): 'low' | 'medium' | 'high' | 'critical' | null {
  const map: Record<string, 'low' | 'medium' | 'high' | 'critical'> = {
    low: 'low', baja: 'low',
    medium: 'medium', media: 'medium', normal: 'medium',
    high: 'high', alta: 'high',
    critical: 'critical', crítica: 'critical', critica: 'critical',
  };
  return map[raw.toLowerCase()] ?? null;
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function BulkTaskUploadModal({
  control,
  onClose,
  technicalReferents,
  addTaskToControl,
  toast,
}: BulkTaskUploadModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ----------------------
  // Descargar plantilla
  // ----------------------
  const downloadTemplate = () => {
    const headers = 'titulo,descripcion,email_responsable,fecha_vencimiento,prioridad';
    const ex1 = '"Implementar política de contraseñas","Crear y documentar la política de contraseñas seguras","responsable@empresa.com","2026-06-01","high"';
    const ex2 = '"Revisar accesos privilegiados","Auditar todas las cuentas con privilegios elevados","otroresponsable@empresa.com","2026-06-15","medium"';
    const csv = [headers, ex1, ex2].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plantilla_tareas_${control.code}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    console.log(`📥 [UI] Plantilla CSV descargada para control ${control.code}`);
  };

  // ----------------------
  // Procesar archivo CSV
  // ----------------------
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (!text) {
        setParseError('El archivo está vacío.');
        return;
      }

      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length < 2) {
        setParseError('El archivo debe tener al menos una fila de datos además del encabezado.');
        return;
      }

      // Validar encabezados (flexible: acepta orden cualquiera siempre que estén todos)
      const headerLine = lines[0].toLowerCase().replace(/"/g, '');
      const expectedHeaders = ['titulo', 'descripcion', 'email_responsable', 'fecha_vencimiento', 'prioridad'];
      const missing = expectedHeaders.filter((h) => !headerLine.includes(h));
      if (missing.length > 0) {
        setParseError(`Faltan columnas requeridas en el encabezado: ${missing.join(', ')}`);
        return;
      }

      // Mapear índices de columna
      const headerCols = parseCSVLine(lines[0].toLowerCase().replace(/"/g, ''));
      const idx = {
        titulo: headerCols.indexOf('titulo'),
        descripcion: headerCols.indexOf('descripcion'),
        email: headerCols.indexOf('email_responsable'),
        fecha: headerCols.indexOf('fecha_vencimiento'),
        prioridad: headerCols.indexOf('prioridad'),
      };

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const rows: ParsedRow[] = lines.slice(1).map((line, i) => {
        const cols = parseCSVLine(line);
        const rowNum = i + 2; // +2 porque la fila 1 es el header
        const errors: string[] = [];

        const titulo = (cols[idx.titulo] ?? '').trim();
        const descripcion = (cols[idx.descripcion] ?? '').trim();
        const emailRaw = (cols[idx.email] ?? '').trim().toLowerCase();
        const fechaRaw = (cols[idx.fecha] ?? '').trim();
        const prioridadRaw = (cols[idx.prioridad] ?? '').trim();

        // Validar título
        if (!titulo) {
          errors.push('El título es requerido.');
        } else if (titulo.length < 3) {
          errors.push('El título debe tener al menos 3 caracteres.');
        }

        // Validar email y buscar referente
        let referent: TechnicalReferent | undefined;
        if (!emailRaw) {
          errors.push('El email del responsable es requerido.');
        } else {
          referent = technicalReferents.find((r) => r.email.toLowerCase() === emailRaw);
          if (!referent) {
            errors.push(`No se encontró un referente activo con email "${emailRaw}".`);
          }
        }

        // Validar fecha
        if (!fechaRaw) {
          errors.push('La fecha de vencimiento es requerida.');
        } else {
          const date = new Date(fechaRaw);
          if (isNaN(date.getTime())) {
            errors.push('Formato de fecha inválido. Use YYYY-MM-DD.');
          } else if (date < today) {
            errors.push('La fecha de vencimiento no puede ser anterior a hoy.');
          }
        }

        // Validar/normalizar prioridad
        const prioridad = prioridadRaw
          ? normalizePriority(prioridadRaw) ?? 'medium'
          : 'medium';
        if (prioridadRaw && !normalizePriority(prioridadRaw)) {
          errors.push(`Prioridad "${prioridadRaw}" no válida. Use: low, medium, high, critical.`);
        }

        return {
          rowNum,
          titulo,
          descripcion,
          email_responsable: emailRaw,
          fecha_vencimiento: fechaRaw,
          prioridad,
          errors,
          referent,
        };
      });

      setParsedRows(rows);
      setStep('preview');
      console.log(`📋 [UI] CSV parseado: ${rows.length} filas (${rows.filter((r) => r.errors.length === 0).length} válidas)`);
    };

    reader.readAsText(file, 'UTF-8');
  };

  // ----------------------
  // Importar tareas
  // ----------------------
  const handleImport = async () => {
    const validRows = parsedRows.filter((r) => r.errors.length === 0 && r.referent);
    if (validRows.length === 0) return;

    setStep('importing');
    setImportProgress(0);
    const results: ImportResult[] = [];

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      setImportProgress(i + 1);

      try {
        const taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'> = {
          title: row.titulo,
          description: row.descripcion,
          status: 'pending',
          assignee: row.referent!,
          dueDate: row.fecha_vencimiento,
          completedAt: undefined,
          checklist: [],
          priority: row.prioridad,
          estimatedHours: 0,
          actualHours: 0,
          emailSent: false,
        };
        await addTaskToControl(control.id, taskData);
        results.push({ rowNum: row.rowNum, titulo: row.titulo, success: true });
        console.log(`✅ [UI] Tarea importada: "${row.titulo}" (fila ${row.rowNum})`);
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'Error desconocido';
        results.push({ rowNum: row.rowNum, titulo: row.titulo, success: false, error: errMsg });
        console.error(`❌ [UI] Error importando fila ${row.rowNum}:`, err);
      }
    }

    setImportResults(results);
    setStep('done');

    const successCount = results.filter((r) => r.success).length;
    const errorCount = results.filter((r) => !r.success).length;

    if (toast) {
      if (errorCount === 0) {
        toast.success(
          `${successCount} tarea${successCount !== 1 ? 's' : ''} importada${successCount !== 1 ? 's' : ''} exitosamente`,
          `Control ${control.code}`
        );
      } else {
        toast.warning(
          `${successCount} importadas, ${errorCount} con errores`,
          `Control ${control.code} — Revise el resumen`
        );
      }
    }
  };

  // ----------------------
  // Valores derivados
  // ----------------------
  const validCount = parsedRows.filter((r) => r.errors.length === 0).length;
  const errorCount = parsedRows.filter((r) => r.errors.length > 0).length;

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              Carga Masiva de Tareas
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Control {control.code} — {control.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Cerrar modal"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Indicador de paso */}
        <div className="px-5 pt-4 flex-shrink-0">
          <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
            {(['upload', 'preview', 'importing', 'done'] as Step[]).map((s, i) => {
              const labels: Record<Step, string> = {
                upload: '1. Subir CSV',
                preview: '2. Previsualizar',
                importing: '3. Importando',
                done: '4. Resumen',
              };
              const isActive = step === s;
              const isPast =
                (['upload', 'preview', 'importing', 'done'] as Step[]).indexOf(step) > i;
              return (
                <React.Fragment key={s}>
                  {i > 0 && <span>›</span>}
                  <span
                    className={
                      isActive
                        ? 'text-blue-600 dark:text-blue-400 font-medium'
                        : isPast
                        ? 'text-green-600 dark:text-green-400'
                        : ''
                    }
                  >
                    {labels[s]}
                  </span>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Contenido scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* ---- STEP: upload ---- */}
          {step === 'upload' && (
            <div className="space-y-4">
              {/* Descargar plantilla */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Paso 1: Descarga la plantilla
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  La plantilla contiene los campos requeridos y dos filas de ejemplo para guiarte.
                </p>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Descargar Plantilla CSV</span>
                </button>
              </div>

              {/* Descripción de columnas */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Formato del CSV
                </h3>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      <th className="pb-1 pr-3">Columna</th>
                      <th className="pb-1 pr-3">Requerido</th>
                      <th className="pb-1">Descripción</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 dark:text-gray-300 space-y-1">
                    {[
                      { col: 'titulo', req: true, desc: 'Título de la tarea (mín. 3 caracteres)' },
                      { col: 'descripcion', req: false, desc: 'Descripción detallada (opcional)' },
                      { col: 'email_responsable', req: true, desc: 'Email de un referente técnico activo' },
                      { col: 'fecha_vencimiento', req: true, desc: 'Fecha límite en formato YYYY-MM-DD' },
                      { col: 'prioridad', req: false, desc: 'low / medium / high / critical (default: medium)' },
                    ].map(({ col, req, desc }) => (
                      <tr key={col} className="border-b border-gray-50 dark:border-gray-700/50">
                        <td className="py-1 pr-3 font-mono text-blue-600 dark:text-blue-400">{col}</td>
                        <td className="py-1 pr-3">
                          {req ? (
                            <span className="text-red-500">✓ Sí</span>
                          ) : (
                            <span className="text-gray-400">No</span>
                          )}
                        </td>
                        <td className="py-1">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Referentes disponibles */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Referentes técnicos disponibles ({technicalReferents.length})
                </h3>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {technicalReferents.map((r) => (
                    <div key={r.id} className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-gray-800 dark:text-gray-200">{r.name}</span>
                      <span>—</span>
                      <span className="font-mono text-blue-600 dark:text-blue-400">{r.email}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Input de archivo */}
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Paso 2: Sube tu archivo CSV
                </h3>
                {parseError && (
                  <p className="text-xs text-red-600 dark:text-red-400 mb-3 flex items-center justify-center space-x-1">
                    <AlertCircle className="w-3 h-3" />
                    <span>{parseError}</span>
                  </p>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center space-x-2 mx-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  <span>Seleccionar archivo CSV</span>
                </button>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Solo archivos .csv</p>
              </div>
            </div>
          )}

          {/* ---- STEP: preview ---- */}
          {step === 'preview' && (
            <div className="space-y-3">
              <div className="flex items-center space-x-4 text-sm">
                <span className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span>{validCount} fila{validCount !== 1 ? 's' : ''} válida{validCount !== 1 ? 's' : ''}</span>
                </span>
                {errorCount > 0 && (
                  <span className="flex items-center space-x-1 text-red-500">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errorCount} con errores (no se importarán)</span>
                  </span>
                )}
              </div>

              <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr className="text-left text-gray-500 dark:text-gray-400">
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Título</th>
                      <th className="px-3 py-2">Responsable</th>
                      <th className="px-3 py-2">Fecha</th>
                      <th className="px-3 py-2">Prioridad</th>
                      <th className="px-3 py-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.map((row) => {
                      const isValid = row.errors.length === 0;
                      return (
                        <tr
                          key={row.rowNum}
                          className={`border-t border-gray-100 dark:border-gray-700 ${
                            isValid
                              ? 'bg-green-50/40 dark:bg-green-900/10'
                              : 'bg-red-50/40 dark:bg-red-900/10'
                          }`}
                        >
                          <td className="px-3 py-2 text-gray-400">{row.rowNum}</td>
                          <td className="px-3 py-2 text-gray-900 dark:text-white max-w-[160px] truncate" title={row.titulo}>
                            {row.titulo || <span className="italic text-gray-400">vacío</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                            {row.referent?.name ?? (
                              <span className="text-red-500">{row.email_responsable || '—'}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row.fecha_vencimiento || '—'}</td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row.prioridad}</td>
                          <td className="px-3 py-2">
                            {isValid ? (
                              <span className="text-green-600 dark:text-green-400 flex items-center space-x-1">
                                <CheckCircle className="w-3 h-3" />
                                <span>OK</span>
                              </span>
                            ) : (
                              <span className="text-red-500" title={row.errors.join('\n')}>
                                <AlertCircle className="w-3 h-3 inline mr-1" />
                                {row.errors[0]}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ---- STEP: importing ---- */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader className="w-8 h-8 text-blue-500 animate-spin" />
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Importando tarea {importProgress} de {parsedRows.filter((r) => r.errors.length === 0).length}...
              </p>
              <div className="w-full max-w-sm bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{
                    width: `${(importProgress / parsedRows.filter((r) => r.errors.length === 0).length) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* ---- STEP: done ---- */}
          {step === 'done' && (
            <div className="space-y-4">
              {/* Resumen */}
              <div className="flex items-center space-x-4 text-sm">
                <span className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span>{importResults.filter((r) => r.success).length} tareas importadas</span>
                </span>
                {importResults.filter((r) => !r.success).length > 0 && (
                  <span className="flex items-center space-x-1 text-red-500">
                    <AlertCircle className="w-4 h-4" />
                    <span>{importResults.filter((r) => !r.success).length} con error</span>
                  </span>
                )}
              </div>

              {/* Filas con error */}
              {importResults.filter((r) => !r.success).length > 0 && (
                <div className="border border-red-200 dark:border-red-800 rounded-lg p-3 space-y-1">
                  <p className="text-xs font-medium text-red-700 dark:text-red-400 mb-2">Errores durante la importación:</p>
                  {importResults
                    .filter((r) => !r.success)
                    .map((r) => (
                      <div key={r.rowNum} className="text-xs text-red-600 dark:text-red-400">
                        Fila {r.rowNum} — {r.titulo}: {r.error}
                      </div>
                    ))}
                </div>
              )}

              {importResults.filter((r) => r.success).length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Las tareas importadas ya están disponibles en el tab de Tareas del control {control.code}.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between flex-shrink-0">
          {step === 'upload' && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Cancelar
            </button>
          )}

          {step === 'preview' && (
            <>
              <button
                onClick={() => {
                  setParsedRows([]);
                  setParseError(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                  setStep('upload');
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                ← Volver
              </button>
              <button
                onClick={handleImport}
                disabled={validCount === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
              >
                <Upload className="w-4 h-4" />
                <span>Importar {validCount} tarea{validCount !== 1 ? 's' : ''}</span>
              </button>
            </>
          )}

          {step === 'done' && (
            <button
              onClick={onClose}
              className="ml-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
            >
              Cerrar
            </button>
          )}

          {step === 'importing' && (
            <span className="text-xs text-gray-400 dark:text-gray-500 self-center">
              Por favor espere...
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
